import React, { useContext, useDeferredValue, useMemo } from 'react';
import { parseContentBlocks } from './parseBlocks';
import MarkdownExtended from '../Library/MarkdowmExtended';
import { Box } from '@mui/material';
import { useLiveTsxMaxCharsForFull } from './liveTsxConfig';
import { AiChatServiceContext } from '../AiChat/AiChatContext';
import { DEFAULT_PART_RENDERERS } from '../AiChat/ChatPane/parts';
import { InteractionPartUtils } from '@lib/models/InteractionPart';
import type { Interaction } from '@lib/models/Interaction';
import type { EventBusService } from '@lib/services/eventBusService';

interface LlmResponseRenderProps {
    /** Full LLM response in markdown (may contain ```jsx code blocks) */
    content: string;
    sessionId: string;
    partial: boolean;
    /** Extra components/values to make available inside JSX blocks */
    scope?: Record<string, unknown>;
    eventBusService?: EventBusService;
    onLinkClicked: (link: string, content: string | null) => void;
    /** The interaction this text belongs to — used to resolve
     *  `{{Component:<slotId>}}` tokens to their UI tool part. */
    message?: Interaction;
    onGoing?: boolean;
    friendlyMode?: boolean;
}

/**
 * Renders an LLM markdown response with live JSX previews.
 *
 * Regular markdown blocks   → MarkdownExtended (syntax highlighting, icons, etc.)
 * ```liveTsx blocks    → compiled at runtime with Sucrase and mounted as React components
 *
 * Usage:
 *   <LlmResponseRender content={llmResponse} sessionId={sessionId} />
 */
const LlmResponseRender: React.FC<LlmResponseRenderProps> = ({
    content,
    sessionId,
    scope,
    partial,
    eventBusService,
    onLinkClicked,
    message,
    onGoing,
    friendlyMode,
}) => {
    const services = useContext(AiChatServiceContext);
    // Host-overridable cap for the placeholder progress bars. Defaults
    // to 2000 chars; ComposerViewV2 bumps it to ~5000 because dashboard
    // snippets routinely run that long.
    const maxCharsForFull = useLiveTsxMaxCharsForFull();
    // Defer `content` so React can keep the previous (cheaper) render on
    // screen while a new chunk's render is still in progress. When the
    // stream emits faster than render can keep up, intermediate values
    // get coalesced — only the latest content actually commits. Combined
    // with the partial-skips-highlighting branch in MarkdownExtended,
    // this keeps the WS handler from being starved during heavy chunks.
    const deferredContent = useDeferredValue(content);
    const blocks = useMemo(
        () => parseContentBlocks(deferredContent, { maxCharsForFull }),
        [deferredContent, maxCharsForFull],
    );

    const hasErrors = blocks.filter(b => (b.code?.indexOf("<!--liveTsx:invalid-->") ?? -1) > -1).length > 0;

    if(hasErrors) return null;

    return blocks.map((block, i) => {
        // `{{Component:<slotId>}}` placement token → render the matching UI tool
        // part here (found in this message by SlotId), in a stable id-keyed
        // container so it mounts once and survives further streamed text.
        if (block.type === 'slot') {
            const uiPart = message?.parts?.find(
                (p) => InteractionPartUtils.uiSlotId(p) === block.slotId,
            );
            // Prefer a host override, else the built-in uiTool dispatcher (which
            // resolves the `uiTools` registry) — so slot-placed UI tools render
            // through the registry too, not only an explicit partRenderers.uiTool.
            const Renderer = services?.partRenderers?.['uiTool'] ?? DEFAULT_PART_RENDERERS['uiTool'];
            if (!uiPart || !Renderer) return null;
            // Grow-in entrance, mirroring LiveJsxBlock's `liveJsxBlockGrowIn`:
            // the component eases up from 0.94 scale + 0 opacity the moment it
            // mounts at its slot. The stable `slot-<id>` key means this fires
            // once — when the token first appears as the prose streams past it —
            // not on every later text append (the block re-derives, the Box
            // keeps its identity, so the animation doesn't replay).
            return (
                <Box
                    key={`slot-${block.slotId}`}
                    sx={{
                        transformOrigin: 'center top',
                        animation: 'uiSlotGrowIn 520ms cubic-bezier(0.16, 1, 0.3, 1) both',
                        '@keyframes uiSlotGrowIn': {
                            '0%': { opacity: 0, transform: 'scale(0.94)' },
                            '100%': { opacity: 1, transform: 'scale(1)' },
                        },
                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }}
                >
                    <Renderer
                        part={uiPart}
                        message={message as Interaction}
                        onGoing={onGoing ?? false}
                        preview={partial}
                        onLinkClicked={onLinkClicked}
                        friendlyMode={friendlyMode}
                    />
                </Box>
            );
        }
        return (
            <MarkdownExtended
                key={i}
                text={block.content ?? ''}
                partial={partial}
                sessionId={sessionId}
                onLinkClicked={onLinkClicked}
            />
        );
    })
}

export default LlmResponseRender;
